import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdCloudUpload, MdCheckCircle, MdError, MdDelete, MdSave, MdAdd } from 'react-icons/md';

const EditEvent = ({ eventId, onBack, onUpdate }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [currentImages, setCurrentImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null

    // Inline Category Creation
    const [showNewCatInput, setShowNewCatInput] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [catLoading, setCatLoading] = useState(false);

    useEffect(() => {
        fetchEventDetails();
        fetchCategories();
    }, [eventId]);

    const fetchEventDetails = async () => {
        try {
            const response = await axios.get(`nai-events/events/${eventId}/`);
            const data = response.data;
            setTitle(data.title);
            setDescription(data.description);
            const formattedDate = data.created_at ? data.created_at.split('T')[0] : '';
            setDate(formattedDate);
            setSelectedCategory(data.category || '');
            setCurrentImages(data.images || []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching event details:", error);
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('nai-events/categories/');
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            setNewImages((prev) => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeNewImage = (index) => {
        setNewImages((prev) => prev.filter((_, i) => i !== index));
    };

    const deleteExistingImage = async (imageId) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;
        try {
            await axios.delete(`nai-events/events/image/${imageId}/delete/`);
            setCurrentImages((prev) => prev.filter((img) => img.id !== imageId));
        } catch (error) {
            console.error("Error deleting image:", error);
            alert("Failed to delete image.");
        }
    };

    const handleAddCategoryInline = async () => {
        if (!newCatName.trim()) return;
        setCatLoading(true);
        try {
            const response = await axios.post('nai-events/categories/', { name: newCatName });
            setCategories((prev) => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
            setSelectedCategory(response.data.id);
            setNewCatName('');
            setShowNewCatInput(false);
        } catch (error) {
            console.error("Error adding category inline:", error);
            alert("Category already exists or invalid data.");
        } finally {
            setCatLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);

        try {
            const eventData = {
                title: title,
                description: description,
                category: selectedCategory || '',
                created_at: date ? new Date(date).toISOString() : null
            };

            await axios.put(`nai-events/events/${eventId}/`, eventData);

            if (newImages.length > 0) {
                const imagesData = new FormData();
                newImages.forEach((image) => {
                    imagesData.append('images', image);
                });
                await axios.post(`nai-events/events/${eventId}/add-images/`, imagesData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setStatus('success');
            setNewImages([]);
            fetchEventDetails();
            setSaving(false);
            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Error updating event:", error);
            setStatus('error');
            setSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-center text-gray-500 font-medium">Loading...</div>;

    return (
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
                <button 
                    onClick={onBack}
                    className="mr-4 p-2.5 rounded-full hover:bg-gray-100 transition-colors"
                >
                    <MdArrowBack className="text-gray-600 w-6 h-6" />
                </button>
                <h2 className="text-2xl font-bold text-gray-800">Edit Event</h2>
            </div>

            {status === 'success' && (
                <div className="mb-6 bg-green-50 border border-green-200 p-4 rounded-xl flex items-center">
                    <MdCheckCircle className="text-green-500 w-6 h-6 mr-3 flex-shrink-0" />
                    <div>
                        <p className="font-semibold text-green-700">Success!</p>
                        <p className="text-sm text-green-600">Event updated successfully.</p>
                    </div>
                </div>
            )}

            {status === 'error' && (
                 <div className="mb-6 bg-red-50 border border-red-200 p-4 rounded-xl flex items-center">
                     <MdError className="text-red-500 w-6 h-6 mr-3 flex-shrink-0" />
                     <div>
                        <p className="font-semibold text-red-700">Error</p>
                        <p className="text-sm text-red-600">Failed to update event. Please try again.</p>
                     </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Event Title</label>
                        <input
                            type="text"
                            required
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Event Date</label>
                        <input
                            type="date"
                            required
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3 text-sm"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                    {!showNewCatInput ? (
                        <div className="flex gap-2">
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="flex-1 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3 text-sm bg-white"
                            >
                                <option value="">-- Select Category (Optional) --</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => setShowNewCatInput(true)}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-4 py-3 rounded-xl border flex items-center gap-1 text-sm transition-colors"
                            >
                                <MdAdd /> Create New
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2 bg-gray-55 p-3 rounded-xl border">
                            <input
                                type="text"
                                placeholder="New Category Name..."
                                value={newCatName}
                                onChange={(e) => setNewCatName(e.target.value)}
                                className="flex-1 rounded-lg border-gray-300 border p-2 text-sm"
                            />
                            <button
                                type="button"
                                disabled={catLoading}
                                onClick={handleAddCategoryInline}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                                {catLoading ? 'Saving...' : 'Add'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowNewCatInput(false)}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Content / Description</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={8}
                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-3 text-sm"
                    />
                </div>

                {/* Existing Images */}
                <div>
                    <h3 className="text-base font-bold text-gray-800 mb-3 border-b pb-1.5">Existing Images ({currentImages.length})</h3>
                    {currentImages.length === 0 ? (
                        <p className="text-gray-500 italic text-sm">No images attached.</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
                            {currentImages.map((img) => (
                                <div key={img.id} className="relative group rounded-xl overflow-hidden border bg-gray-50">
                                    <img 
                                        src={img.image} 
                                        alt="Event Photo" 
                                        className="w-full h-24 object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => deleteExistingImage(img.id)}
                                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full shadow hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Delete Image"
                                    >
                                        <MdDelete size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add More Images */}
                <div className="space-y-4">
                    <h3 className="text-base font-bold text-gray-800 mb-3 border-b pb-1.5">Add More Images</h3>
                    <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-400 transition-colors">
                        <div className="space-y-1 text-center">
                            <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600 justify-center">
                                <label htmlFor="new-events-upload" className="relative cursor-pointer bg-white rounded-md font-semibold text-blue-600 hover:text-blue-500 focus-within:outline-none">
                                    <span>Upload files</span>
                                    <input 
                                        id="new-events-upload" 
                                        name="new-events-upload" 
                                        type="file" 
                                        className="sr-only" 
                                        onChange={handleImageChange} 
                                        accept="image/*" 
                                        multiple 
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                    {/* New Images Preview */}
                    {newImages.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
                            {newImages.map((file, index) => (
                                <div key={index} className="relative group rounded-lg overflow-hidden border bg-gray-50 p-1 flex flex-col justify-between">
                                    <div className="w-full h-16 bg-blue-50 rounded flex items-center justify-center text-[10px] text-center break-all p-1 text-blue-700 font-medium overflow-hidden">
                                        {file.name}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeNewImage(index)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors"
                                    >
                                        <MdDelete className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-4 border-t">
                    <button
                        type="submit"
                        disabled={saving}
                        className={`inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        <MdSave className="mr-2" />
                        {saving ? 'Saving Changes...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditEvent;
